import { Box, Typography } from "@mui/material";
import { CheckinIcon, CheckOutIcon } from "../../../../../common/icons/Icons";
import { dateUtils } from "../../../../../utils/DateUtils";
import { forwardRef, useEffect, useMemo, useState } from "react";
import DayCard from "./StayDetails/DayCard";
import { validated } from "../../../../../common/inputs/ValidatedInputs";
import AccommodationCard from "./StayDetails/AccommodationCard";
import { withQueryData } from "../../../../../common/withQueryData";
import { useRoomsData } from "../../../../../common/hooks/useRoomsData";
import { useDailyAvailabilityData } from "../../../../../common/hooks/useAvailabilityAndPricingData";
import { MAX_RESERVATION_NIGHTS } from "../../../../../common/bookingConstants";

const cardStyle = {
    '& .MuiTypography-root': {
        margin: '5px 0px',
        textAlign: 'center',
    },
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 0px',
    alignItems: 'center',
}

const ENUM_LOADING_STATUS = {
    LOADING_AVAILABILITY: 'Loading availability...',
    LOADING_ROOM_DEFINITION: 'Loading room definition...',
    LOADING_ROOMS: 'Loading rooms...'
}

const getInitAvailabilitySpan = (reservation) => {
    const from = new Date(reservation.checkin.getFullYear(), reservation.checkin.getMonth() - 2, 1);//1st day of 2 months before
    const to = new Date(reservation.checkin.getFullYear(), reservation.checkin.getMonth() + 3, 0);//last day of 2 months after

    return {
        from,
        to,
    }
}

const ValidatedDayCard = validated(DayCard);

const StayDetails = forwardRef((props, ref) => {
    const { propertyId } = props;

    const { queryResults } = useRoomsData(propertyId);

    return <StayDetailsContentWithQueryData
        queryData={queryResults}
        {...props}
    />
})

const StayDetailsContent = ({ data: rooms, reload, editing, reservation, originalReservation, propertyId, onChange, onError, setLoadingStatus, setEditing }) => {
    const { checkin, checkout } = reservation;

    const [availabilitySpan, setAvailabilitySpan] = useState(getInitAvailabilitySpan(reservation));//eslint-disable-line no-unused-vars

    const room = rooms.find(room => room.id === reservation.roomId);

    const { queryResults } = useDailyAvailabilityData(propertyId, room?.id, availabilitySpan.from, availabilitySpan.to, editing && !!room);
    const { data: roomAvailability, error: availabilityError, isLoading: isLoadingAvailability, isError: isAvailabilityError, refetch: refetchAvailability, isFetching: isFetchingAvailability } = queryResults;//eslint-disable-line no-unused-vars

    const checkinAvailability = useMemo(() => {
        if (!roomAvailability) {
            return null;
        }

        let _availability = { ...roomAvailability };

        for (const date = new Date(originalReservation.checkin.getTime()); date <= originalReservation.checkout; date.setDate(date.getDate() + 1)) {
            if (reservation.roomId === originalReservation.roomId) {
                _availability && (_availability[dateUtils.getDateString(date)] += 1);
            }
        }

        return _availability;
    }, [roomAvailability, reservation.roomId, originalReservation]);

    useEffect(() => {
        if (isLoadingAvailability) {
            setLoadingStatus(ENUM_LOADING_STATUS.LOADING_AVAILABILITY);
        }
        else {
            setLoadingStatus(null);
        }
    }, [isLoadingAvailability, setLoadingStatus]);

    const lengthOfStay = dateUtils.getLengthOfStay(checkin, checkout);

    const minCheckout = new Date(checkin.getTime());
    minCheckout.setDate(minCheckout.getDate() + 1);
    minCheckout.setHours(checkout.getHours());

    const maxCheckout = useMemo(() => {
        if (!checkinAvailability) {
            return null;
        }

        let _maxCheckout = new Date(checkin.getTime());

        let los = 0;

        do {
            los++;
            _maxCheckout.setDate(_maxCheckout.getDate() + 1);
        }
        while (checkinAvailability && checkinAvailability[dateUtils.getDateString(_maxCheckout)] > 0 && los <= MAX_RESERVATION_NIGHTS);

        return _maxCheckout;
    }, [checkin, checkinAvailability]);

    const editModeReady = editing && !isLoadingAvailability && !isFetchingAvailability && !isAvailabilityError;

    return <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'space-around', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: '10px' }}>
            <ValidatedDayCard
                id='checkin'
                key='checkin'
                value={checkin}
                onChange={onChange}
                editing={editModeReady}
                Icon={CheckinIcon}
                availabilityLoaded={!!checkinAvailability}
                minDate={checkinAvailability && new Date(Object.keys(checkinAvailability)[0])}
                maxDate={checkinAvailability && new Date(Object.keys(checkinAvailability)[Object.keys(checkinAvailability).length - 1])}
                shouldDisableDate={date => checkinAvailability && checkinAvailability[dateUtils.getDateString(date)] === 0}
                date
                calendarProps={{
                    initialHighlight: {
                        start: checkin,
                        end: checkout,
                    },
                }}
                cardStyle={{
                    ...cardStyle,
                    minWidth: '165px',
                }}
                getErrorMessage={reason => {
                    switch (reason) {
                        case "dateDisabled":
                            return "Check-in not available on selected date";
                        default:
                            return reason;
                    }
                }}
            />
            <Box sx={{ ...cardStyle, justifyContent: 'center' }}>
                <Typography sx={{ height: '24px' }}>{lengthOfStay > 0 && `${lengthOfStay} night${lengthOfStay > 1 ? 's' : ''}`}</Typography>
                <Typography variant='h2'>&#10230;</Typography>
            </Box>
            <ValidatedDayCard
                id='checkout'
                key='checkout'
                value={checkout}
                onChange={onChange}
                editing={editModeReady}
                Icon={CheckOutIcon}
                availabilityLoaded={!!checkinAvailability}
                minDate={minCheckout}
                maxDate={maxCheckout}
                date
                calendarProps={{
                    initialHighlight: {
                        start: checkin,
                        end: checkout,
                    },
                }}
                cardStyle={{
                    ...cardStyle,
                    minWidth: '165px',
                }}
                getErrorMessage={reason => {
                    switch (reason) {
                        case "minDate":
                            return "Check-out must be after check-in";
                        case "maxDate":
                            return "Check-out not available on selected date";
                        default:
                            return reason;
                    }
                }}
            />
        </Box>
        <AccommodationCard
            propertyId={propertyId}
            reservation={reservation}
            editing={editModeReady}
            onRoomSelect={(roomId) => onChange('roomId', roomId)}
            onOccupancyChange={(onChange)}
            cardStyle={cardStyle}
            availableRooms={rooms}
        />
    </Box>
}

const StayDetailsContentWithQueryData = withQueryData(StayDetailsContent)

export default StayDetails;